import { CountUp } from "../../../effects/CountUp";
import { askAboutAndReveal } from "../../../state/chatActions";
import { acquirerBand } from "../../../data/acquirer";
import type { MonthlyRow, Portfolio } from "../../../data/types";
import { fmtEur, fmtGbp, fmtPct } from "./format";
import styles from "./HeadlineStats.module.css";

interface HeadlineStatsProps {
  portfolio: Portfolio;
}

const BAND_COPY: Record<
  ReturnType<typeof acquirerBand>,
  { label: string; tone: "good" | "warn" | "bad" }
> = {
  healthy: { label: "healthy", tone: "good" },
  above_standard: { label: "above standard", tone: "warn" },
  excessive: { label: "excessive", tone: "bad" },
};

export function HeadlineStats({ portfolio }: HeadlineStatsProps) {
  const acq = portfolio.acquirer;
  const rollingPct = rollingVampPct(portfolio.monthly);
  const band = acquirerBand(rollingPct);
  const bandCopy = BAND_COPY[band];
  const headroomAs = Math.max(0, acq.vampAboveStandard - rollingPct);
  const headroomEx = Math.max(0, acq.vampExcessive - rollingPct);

  return (
    <div className={styles.card}>
      <header className={styles.header}>
        <span className={styles.title}>portfolio pulse</span>
        <span className={styles.muted}>
          weighted across {portfolio.merchants.length} merchants · rolling 12mo
        </span>
      </header>

      <div className={styles.grid}>
        <button
          type="button"
          className={`${styles.cell} ${styles.vamp}`}
          onClick={() =>
            askAboutAndReveal(
              `the portfolio vamp is ${fmtPct(rollingPct)} rolling. break down which merchants and mccs contribute most to that number, and what the trend has been over the last 3 months.`
            )
          }
          title="ask about the portfolio vamp"
        >
          <span className={styles.cellLabel}>portfolio vamp</span>
          <span className={styles.cellValue}>{fmtPct(rollingPct)}</span>
          <span className={styles.cellSub}>
            acquirer-level · rolling 12mo
          </span>
        </button>

        <button
          type="button"
          className={`${styles.cell} ${styles.headroom} ${styles[`tone-${bandCopy.tone}`]}`}
          onClick={() =>
            askAboutAndReveal(
              `we're in the ${bandCopy.label} band at ${fmtPct(rollingPct)}. above-standard sits at ${fmtPct(acq.vampAboveStandard, 2)} (${fmtPct(headroomAs)} away) and excessive at ${fmtPct(acq.vampExcessive, 2)} (${fmtPct(headroomEx)} away). which merchants are pushing us up fastest?`
            )
          }
          title="ask about the vamp band"
        >
          <span className={styles.cellLabel}>band</span>
          <span className={styles.cellValue}>{bandCopy.label}</span>
          <span className={styles.cellSub}>
            above-std ≥ {fmtPct(acq.vampAboveStandard, 2)} · excessive ≥{" "}
            {fmtPct(acq.vampExcessive, 2)}
          </span>
        </button>

        <button
          type="button"
          className={`${styles.cell} ${styles.secured}`}
          onClick={() =>
            askAboutAndReveal(
              `walk me through the ${fmtEur(portfolio.weekSecuredEur)} we've secured this week — which interventions and merchants contributed?`
            )
          }
          title="ask about this week's secured value"
        >
          <span className={styles.cellLabel}>secured this week</span>
          <span className={styles.cellValue}>
            <CountUp
              value={portfolio.weekSecuredEur}
              format={(n) => fmtEur(n)}
              durationMs={900}
            />
          </span>
          <span className={styles.cellSub}>
            {portfolio.interventionsWeek.accepted}/
            {portfolio.interventionsWeek.fired} interventions · tc40 suppressed{" "}
            {fmtGbp(portfolio.tc40SuppressedGbpWeek)}
          </span>
        </button>

        <button
          type="button"
          className={`${styles.cell} ${styles.ytd}`}
          onClick={() =>
            askAboutAndReveal(
              `break down the ${fmtEur(portfolio.ytdSecuredEur)} ytd secured — how is that distributed across merchants and intervention types?`
            )
          }
          title="ask about ytd secured"
        >
          <span className={styles.cellLabel}>secured ytd</span>
          <span className={styles.cellValue}>
            <CountUp
              value={portfolio.ytdSecuredEur}
              format={(n) => fmtEur(n)}
              durationMs={1100}
            />
          </span>
          <span className={styles.cellSub}>attributed + signed by ripper</span>
        </button>
      </div>
    </div>
  );
}

function rollingVampPct(monthly: MonthlyRow[]): number {
  let tc = 0;
  let purchases = 0;
  for (const r of monthly) {
    if (!r.active) continue;
    tc += r.tc40 + r.tc15;
    purchases += r.purchases;
  }
  if (purchases === 0) return 0;
  return Math.round(((tc / purchases) * 100) * 1000) / 1000;
}
